<template>
    <b-form-group :label="label" :description="description" :invalid-feedback="error" :state="state">
        <section class="input-wrapper">
            <vue-tel-input
                v-on="listeners"
                v-bind="attrs"
                mode="national"
                :autoDefaultCountry="false"
                :model-value="formattedValue"
                :preferred-countries="preferredCountries"
                :inputOptions="{
                    placeholder: floatingLabel,
                    styleClasses: [floatingLabel ? 'form-phone-number-input-label' : '', 'form-phone-number-input'],
                }"
                :styleClasses="[
                    { 'wrapper-height': floatingLabel },
                    'form-phone-number-input-wrapper',
                    error ? 'form-phone-number-input-invalid' : '',
                ]"
                :default-country="defaultCountryCode"
                :key="vueTelInputKey"
                @on-input="input"
                @focus="isInputFocused = true"
                @blur="isInputFocused = false"
            ></vue-tel-input>
            <label class="floating-label" :if="floatingLabel" :class="{ 'active-label': value || isInputFocused }">{{
                floatingLabel
            }}</label>
        </section>
    </b-form-group>
</template>

<script>
    import { parsePhoneNumber } from 'awesome-phonenumber';
    import { VueTelInput } from 'vue-tel-input';
    import { formFieldMixin } from '../../../mixins/form-field-mixin';
    import 'vue-tel-input/vue-tel-input.css';

    export default {
        name: 'FormPhoneNumberInput',
        mixins: [formFieldMixin],
        components: {
            VueTelInput,
        },
        props: {
            value: [String, Number],
            preferredCountries: {
                type: Array,
                default: () => ['US', 'GB'],
            },
            defaultCountryCode: {
                type: String,
            },
            floatingLabel: [String, Number],
        },
        data() {
            return {
                isInputFocused: false,
                vueTelInputKey: 0,
            };
        },
        computed: {
            formattedValue() {
                const phone = parsePhoneNumber(this.value);
                if (phone.isValid()) {
                    return phone.getNumber('national');
                }
                return this.value;
            },
        },
        methods: {
            input(phone, phoneObject) {
                if (phoneObject.valid) {
                    this.$emit('input', phoneObject.number, phoneObject.valid);
                } else {
                    this.$emit('input', phone);
                }
            },
        },
        watch: {
            defaultCountryCode() {
                if (!this.value) {
                    this.vueTelInputKey += 1;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    @import '../../../mixins.scss';
    @import '../../../variables.scss';

    :deep(.form-phone-number-input-wrapper) {
        @include transition(all ease-in-out 0.2s);
        border: $input-border-width solid $input-border-color;
        height: $input-height !important;
        .form-phone-number-input {
            overflow-x: hidden;
        }
    }

    :deep(.wrapper-height) {
        height: calc(1.9em + 0.75rem + 4px) !important;
    }

    :deep(.form-phone-number-input-invalid) {
        border-color: $form-feedback-invalid-color;

        @if $enable-validation-icons {
            padding-right: $input-height-inner;
            background-image: escape-svg($form-feedback-icon-invalid);
            background-repeat: no-repeat;
            background-position: right $input-height-inner-quarter center;
            background-size: $input-height-inner-half $input-height-inner-half;
        }

        &:focus-within {
            border-color: $form-feedback-invalid-color !important;
            box-shadow: 0 0 0 $input-focus-width rgba($form-feedback-invalid-color, 0.25) !important;
        }
    }

    @mixin activeInput() {
        padding-bottom: 1px;
        padding-top: 14px;
        z-index: inherit;
        height: $floating-label-input-height-active;
    }

    @mixin activeLabel() {
        font-size: 0.8rem;
        line-height: 1.1;
        padding: 0.1rem;
        margin: 0;
        color: $floating-label-color-active;
        font-weight: $floating-label-font-weight;
        top: 0.1rem;
    }

    :deep(.form-phone-number-input) {
        @include transition(all ease-in-out 0.2s);
        color: $floating-label-placeholder-color;

        &::placeholder {
            color: $input-placeholder-color;
            opacity: 1;
        }
    }

    :deep(.form-phone-number-input-label) {
        background: transparent;
        @include disablePlaceholder;
        &:not(:placeholder-shown),
        &:focus:not(:placeholder-shown) {
            @include activeInput;
        }

        &:focus {
            @include activeInput;
        }
    }

    :deep(.vti__dropdown-list) {
        z-index: 999;
    }

    .input-wrapper {
        position: relative;
    }

    .floating-label {
        @include transition(all ease-in-out 0.2s);
        position: absolute;
        left: 4.4rem;
        z-index: 99;
        user-select: none;
        pointer-events: none;
        top: 0;
        font-size: inherit;
        line-height: 1.5;
        padding: 0.8rem 0.1rem;
        margin: 0;
        color: $floating-label-placeholder-color;
    }

    :deep(.active-label) {
        @include activeLabel;
    }

    @include media-breakpoint-down(md) {
        .floating-label {
            left: 4rem;
        }
    }
</style>
